import { useEffect, useMemo, useRef, useState } from "react";
import { getNewsSortTime } from "../utils/newsSort";
import { getSourceMapValue } from "../utils/sourceModel";

export const useQuickAccessSources = ({
  allNews,
  availableSources,
  activeSources,
  unreadCounts,
  focusSource,
}) => {
  const frozenFocusSourceRef = useRef(null);
  const [frozenQuickAccessSources, setFrozenQuickAccessSources] = useState(null);

  const sortedAllSources = useMemo(() => {
    const latestTimeMap = new Map();

    allNews.forEach((item) => {
      const time = getNewsSortTime(item);
      const current = latestTimeMap.get(item.source);

      if (current === undefined || time > current) {
        latestTimeMap.set(item.source, time);
      }
    });

    return [...availableSources].sort((a, b) => {
      const aActive = activeSources ? activeSources.includes(a) : false;
      const bActive = activeSources ? activeSources.includes(b) : false;

      if (aActive !== bActive) return aActive ? -1 : 1;

      const unreadA = getSourceMapValue(unreadCounts, a, 0) || 0;
      const unreadB = getSourceMapValue(unreadCounts, b, 0) || 0;

      if (unreadA !== unreadB) return unreadB - unreadA;

      const timeA = latestTimeMap.get(a) || 0;
      const timeB = latestTimeMap.get(b) || 0;

      return timeB - timeA;
    });
  }, [activeSources, allNews, availableSources, unreadCounts]);

  useEffect(() => {
    if (focusSource !== null) {
      if (frozenFocusSourceRef.current !== focusSource) {
        setFrozenQuickAccessSources(
          sortedAllSources.filter((source) => activeSources?.includes(source))
        );

        frozenFocusSourceRef.current = focusSource;
      }

      return;
    }

    setFrozenQuickAccessSources(null);
    frozenFocusSourceRef.current = null;
  }, [activeSources, focusSource, sortedAllSources]);

  const quickAccessSources = useMemo(() => {
    if (focusSource !== null && frozenQuickAccessSources) {
      return frozenQuickAccessSources;
    }

    if (!activeSources) return [];

    return sortedAllSources.filter((source) => activeSources.includes(source));
  }, [activeSources, focusSource, frozenQuickAccessSources, sortedAllSources]);

  return quickAccessSources;
};
